export const durationUnits = [
	{ value: 'months', label: 'Months', seconds: 30 * 86400 },
	{ value: 'weeks', label: 'Weeks', seconds: 7 * 86400 },
	{ value: 'days', label: 'Days', seconds: 86400 },
	{ value: 'hours', label: 'Hours', seconds: 3600 },
	{ value: 'minutes', label: 'Minutes', seconds: 60 },
	{ value: 'seconds', label: 'Seconds', seconds: 1 },
] as const;

export type DurationUnit = typeof durationUnits[number]['value'];
export type DurationMode = DurationUnit | 'custom' | 'none';
export type DurationParts = { days: string; hours: string; minutes: string; seconds: string };
const maximumWholeSeconds = 922337203685;

export function parseDuration(value?: string | null): DurationParts | undefined {
	const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2}(?:\.\d{1,7})?)$/.exec(value ?? '');
	if (!match) return undefined;
	return { days: match[1] ?? '0', hours: String(Number(match[2])), minutes: String(Number(match[3])), seconds: match[4].replace(/^0(?=\d)/, '') };
}

export function formatDurationParts(parts: DurationParts): string | undefined {
	if (![parts.days, parts.hours, parts.minutes].every(value => /^\d+$/.test(value)) || !/^\d+(?:\.\d{1,7})?$/.test(parts.seconds)) return undefined;
	const { days, hours, minutes, seconds } = parts;
	if (Number(hours) > 23 || Number(minutes) > 59 || Number(seconds) >= 60) return undefined;
	const wholeSeconds = Number(days) * 86400 + Number(hours) * 3600 + Number(minutes) * 60 + Math.floor(Number(seconds));
	if (!Number.isSafeInteger(wholeSeconds) || wholeSeconds > maximumWholeSeconds) return undefined;
	const fraction = (seconds.split('.')[1] ?? '').padEnd(7, '0');
	if (wholeSeconds === maximumWholeSeconds && fraction > '4775807') return undefined;
	const [whole, decimal] = seconds.split('.');
	const time = `${String(Number(hours)).padStart(2, '0')}:${String(Number(minutes)).padStart(2, '0')}:${String(Number(whole)).padStart(2, '0')}${decimal ? `.${decimal}` : ''}`;
	return Number(days) > 0 ? `${Number(days)}.${time}` : time;
}

export function formatDurationAmount(amount: string, unit: DurationUnit): string | undefined {
	if (!/^\d+$/.test(amount)) return undefined;
	const total = Number(amount) * durationUnits.find(item => item.value === unit)!.seconds;
	if (!Number.isSafeInteger(total) || total > maximumWholeSeconds) return undefined;
	return formatDurationParts({ days: String(Math.floor(total / 86400)), hours: String(Math.floor(total / 3600) % 24), minutes: String(Math.floor(total / 60) % 60), seconds: String(total % 60) });
}

export function compactDuration(value: string | null | undefined, optional: boolean): { mode: DurationMode; amount: string } {
	if (value == null && optional) return { mode: 'none', amount: '1' };
	if (value == null) return { mode: 'seconds', amount: '0' };
	const parts = parseDuration(value);
	if (!parts || !formatDurationParts(parts)) return { mode: 'custom', amount: '' };
	const seconds = Number(parts.seconds);
	if (!Number.isInteger(seconds)) return { mode: 'custom', amount: '' };
	const total = Number(parts.days) * 86400 + Number(parts.hours) * 3600 + Number(parts.minutes) * 60 + seconds;
	const unit = total === 0 ? durationUnits[durationUnits.length - 1] : durationUnits.find(item => total % item.seconds === 0)!;
	return { mode: unit.value, amount: String(total / unit.seconds) };
}
